import { ErrorHandler } from "UI/Errors";
import { CaseInsensitiveMap } from "../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { Event } from "../../../../Global/Base/Events";
import { InvalidArgumentException, InvalidOperationException } from "../../../../Global/Base/Exceptions";
import { CancelError } from "../../../../Global/Base/Promises/CancellationTokenSource";
import { AttrValueReader } from "../../../../OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IAttributeControl, IGenericControl, IHTMLElementWithControl, IRequiresActualObject } from "../../UI/Controls/ControlInterfaces";
import { IContentBuilder } from "../../UI/DataBuilders/IContentBuilder";
import { LocalizedStringBuilder } from "../../UI/DataBuilders/LocalizedStringBuilder";
import { StringBuilder } from "../../UI/DataBuilders/StringBuilder";

/**
 * @internal
 */
export class SectionHandler {
    private __obj: Promise<OWObject>;
    private readonly __objCreator: () => Promise<OWObject>;
    private readonly _saveFunction: (obj: OWObject) => Promise<void>;
    private _controls: (IAttributeControl & IGenericControl)[] = [];
    private _controlsByAttrName: CaseInsensitiveMap<IAttributeControl & IGenericControl> = new CaseInsensitiveMap<IAttributeControl & IGenericControl>();
    private _pendingUpdate: Promise<any>;
    private _resetChangedStateAttrs: Set<string> = new Set();
    private __isChanged: boolean = false;

    protected _showErrorFunction: (content: IContentBuilder | null) => Promise<void>;

    public readonly changed: Event<SectionHandler, boolean> = new Event<SectionHandler, boolean>();

    protected set _isChanged(v: boolean) {
        if (v === this.__isChanged)
            return;

        this.__isChanged = v;
        this.changed.trigger(this, v);
    }

    public get isChanged(): boolean {
        return this.__isChanged;
    }

    protected get _obj(): Promise<OWObject> {
        if (this.__obj === undefined) {
            this.__obj = this.__objCreator()?.then(async obj => {
                obj.attributeChanged.add((sender, attrName) => this._attributeChanged(sender, attrName));
                await this._updateDisplayedValues(obj);
                return obj;
            }).catch((e: Error) => {
                ErrorHandler.instance.handleError(e);
                return null;
            });
        }

        return this.__obj;
    }

    /**
     * Obsluha stránky s vlastním objektem
     * @param rootElement Kořenový HTML element sekce
     * @param createObjectFunction Funkce pro získání nového záznamu
     * @param saveFunction Funkce pro uložení záznamu
     * @param showErrorFunction Funkce pro zobrazení validačních chyb
     * @param roles Role uživatele
     */
    constructor(rootElement: Element,
        createObjectFunction: () => Promise<OWObject>,
        saveFunction: (obj: OWObject) => Promise<void>,
        showErrorFunction: (content: IContentBuilder | null) => Promise<void>,
        roles?: readonly string[]) {
        if (!rootElement)
            throw new InvalidArgumentException("rootElement");
        if (!createObjectFunction)
            throw new InvalidArgumentException("obj");
        if (!saveFunction)
            throw new InvalidArgumentException("saveFunction");
        if (!showErrorFunction)
            throw new InvalidArgumentException("showErrorFunction");

        this.__objCreator = createObjectFunction;
        this._saveFunction = saveFunction;
        this._showErrorFunction = showErrorFunction;

        const controlElements = Array.from(rootElement.querySelectorAll("[data-behaviour=component]")) as IHTMLElementWithControl[];
        for (const controlElement of controlElements) {
            const control = controlElement.tscControl;

            if (!(control instanceof IAttributeControl))
                throw new InvalidOperationException(
                    `Control '${controlElement.getAttribute("data-tsccontrol")}' id ${controlElement.id} is not IAttributeControl`); //`

            control.attrName = controlElement.getAttribute("data-attrname");

            if (controlElement.hasAttribute("data-required"))
                controlElement.classList.add("Required");

            if (controlElement.hasAttribute("data-resetchanged"))
                this._resetChangedStateAttrs.add(control.attrName.toLowerCase());

            control.valueChanged.add((sender) => {
                if (control.attrName)
                    this._sendValue(sender.attrName, sender.controlValue, roles);
                else
                    this._isChanged = true;
            });

            this._controls.push(control);

            if (control.attrName)
                this._controlsByAttrName.set(control.attrName, control);
        }
    }

    protected async _sendValue(attributeName: string, value: any, _roles?: readonly string[]): Promise<void> {
        this._isChanged = !this._resetChangedStateAttrs.has(attributeName.toLowerCase()); // některé komponenty jsou nastavené tak, že resetují změněný stav - třeba nadřízení LOV přenačte změny v podřízeném MSLOV a zahodí je

        if (this._pendingUpdate)
            await this._pendingUpdate;

        try {
            //TODO #32434 zkontrolovat, jestli opravdu nepotřebuje kontext
            this._pendingUpdate = this._obj.then(obj => obj.setValue(attributeName, value));
            await this._pendingUpdate;
        } catch (exception) {
            if (!(exception instanceof CancelError))
                ErrorHandler.instance.handleError(exception);
        }
    }

    public async save(_roles?: readonly number[]): Promise<boolean> {
        if (this._pendingUpdate)
            await this._pendingUpdate;

        if (!this.isChanged)
            return true;

        try {
            if (this._checkRequirements()) {
                const obj = await this._obj;

                if (obj)
                    await obj.validate(); // nic nevrací, ale vyhazuje výjimky při chybě

                await this._saveFunction(obj);

                this._isChanged = false;
                this._showErrorFunction(null);
                return true;
            }
        } catch (e) {
            this._showErrorFunction(new StringBuilder(e.toString()));
        }
        return false;
    }

    protected _checkRequirements(): boolean {
        if (this._controls.some(control => control.element.hasAttribute("data-required") && !control.controlValue)) {
            this._showErrorFunction(new LocalizedStringBuilder("GUI-1000")); // "Nevyplněné povinné pole"
            return false;
        }
        return true;
    }

    protected async _attributeChanged(obj: OWObject, attrName: string): Promise<void> {
        const control = this._controlsByAttrName.get(attrName);
        if (!control)
            return;

        control.controlValue = await obj.getValue(attrName);
    }

    private async _updateDisplayedValues(obj: OWObject): Promise<void> {
        for (const control of this._controls) {
            if (control instanceof IRequiresActualObject)
                control.actualObject = obj;

            const val = await obj.getValue(control.attrName);
            control.controlValue = AttrValueReader.read(val);
        }
    }
}
